import { Component, Input } from "@angular/core";
import {
  LoadingController,
  ModalController,
  PopoverController,
} from "ionic-angular";
import ContentWrapper from "../../../models/content-wrapper/content-wrapper";
import SortOptions from "../../../models/content-wrapper/sort-options";
import { PatientHistory } from "../../../models/patient-chart/patient-history/patient-history";
import { PatientHistoryGridModel } from "../../../models/patient-chart/patient-history/patient-history-grid-model";
import { EditViewPopoverPage } from "../../../navigation/components/edit-view-popover/edit-view-popover";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { PatientHistoryServiceProvider } from "../../../providers/patient-charts/patient-history-service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";

@Component({
  selector: "patient-history-grid",
  templateUrl: "patient-history-grid.html",
})
export class PatientHistoryGridComponent {
  @Input()
  set historyType(inputHistoryType: string) {
    if (inputHistoryType) {
      this._historyType = inputHistoryType;
    }
  }

  @Input()
  set patientId(inputPatientId: number) {
    if (inputPatientId) {
      this._patientId = inputPatientId;
    }
  }

  @Input()
  set disableAdd(inputDisableAdd: boolean) {
    if (inputDisableAdd) {
      this._disableAdd = inputDisableAdd;
    }
  }

  @Input()
  set disableEdit(inputDisableEdit: boolean) {
    if (inputDisableEdit) {
      this._disableEdit = inputDisableEdit;
    }
  }

  @Input()
  set disableDelete(inputDisableDelete: boolean) {
    if (inputDisableDelete) {
      this._disableDelete = inputDisableDelete;
    }
  }

  @Input()
  set toDate(inputToDate: string) {
    if (inputToDate) {
      this._toDate = inputToDate;
    }
  }

  @Input()
  set externalId(externalId: boolean) {
    if (externalId) {
      this._externalId = externalId;
    }
  }

  /* Grid variables */
  _historyType: string; // options: 'medical', 'family', 'social'
  _patientId: number = 0;
  addButtonID: string = "addPatientHistoryButton";
  moreButtonID: string = "morePatientHistoryButton";
  _disableAdd: boolean = false;
  _disableEdit: boolean = false;
  _disableDelete: boolean = false;
  _externalId: boolean = false;
  _toDate: string = "";
  gridDataArray: PatientHistory[] = [];
  gridHeaderColumnObj = new PatientHistoryGridModel();
  gridHeaderColumns: string[] = [];

  /* Pagination/Sorting variables */
  currentPage: number = 0;
  totalPageCount: number = 0;
  pageLimit: number = 5;
  sortOptions: SortOptions = new SortOptions("createdDatetime", "desc"); // default sort settings

  /* Misc variables */
  spinner: any = null;
  spinnerCounter: number = 0;

  /* Constants */
  POPOVER_PAGE = "PatientHistoryPopoverPage";

  constructor(
    public loadingCtrl: LoadingController,
    public modalCtrl: ModalController,
    public patientHistoryService: PatientHistoryServiceProvider,
    public utilService: UtilityService,
    private popover: PopoverController,
    private navBarService: NavBarServiceProvider,
    public resourceManagementService: ResourceManagementService
  ) {
    // retrieve property names of display model to use to print column headers - allows for dynamic HTML
    this.gridHeaderColumns = Object.keys(new PatientHistoryGridModel());
  }

  ngOnInit(): void {
    this.getPatientHistoryData();
    this.addButtonID = "addHistoryButton-" + this._historyType;
    this.moreButtonID = "moreHistoryButton-" + this._historyType;
  }

  // opens patient-history pop-up, reloads grid if successfully saved
  openPopOver($page, isEditMode: boolean, patientHistoryObj: PatientHistory) {
    let addEditModal = this.modalCtrl.create(
      $page,
      {
        editMode: isEditMode,
        patientId: this._patientId,
        patientHistoryObj: patientHistoryObj,
        externalId: this._externalId,
        historyEventType: this._historyType,
      },
      { enableBackdropDismiss: false }
    );
    addEditModal.onDidDismiss((data) => {
      if (data && data.status === "Complete") {
        this.getPatientHistoryData();
      }
    });
    addEditModal.present();
  }

  // called when column header is clicked for sorting
  // sets sortOptions then invokes GET endpoint based on these settings
  sortBy(selectedColumn: string) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedColumn) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedColumn;
      this.sortOptions.direction = "asc";
    }
    this.getPatientHistoryData();
  }

  // Invokes GET endpoint based on sort/pagination settings
  getPatientHistoryData(): void {
    this.increaseSpinnerCount("Loading...");
    this.patientHistoryService
      .getPatientHistoryData(
        this._patientId,
        this.currentPage,
        this.pageLimit,
        this.sortOptions.direction,
        this.sortOptions.column,
        this._historyType,
        this._toDate,
        this._externalId
      )
      .subscribe(
        (response: ContentWrapper<PatientHistory>) => {
          this.generateGridData(response);
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.decreaseSpinnerCount();
          console.error(err);
        }
      );
  }

  // Populates gridDataArray by mapping GET 'getPatientHistoryData' response data to PatientHistoryGridModel objects.
  generateGridData(historyResponseData: ContentWrapper<PatientHistory>) {
    this.totalPageCount = historyResponseData.totalPages;

    this.gridDataArray = historyResponseData.content.map(
      (history: PatientHistory) => {
        return new PatientHistory(
          history.id,
          history.historyEventType,
          history.subject,
          history.description,
          history.eventDate,
          history.createdDatetime,
          history.createdBy,
          history.updatedDatetime,
          history.updatedBy,
          history.createdById,
          history.updatedById
        );
      }
    );

    // this.setCredentialsToCreatedBy(this.gridDataArray);
  }

  // // if history has createdById pull credentials from RM for createdBy, else leave as it is
  // setCredentialsToCreatedBy(gridDataArray: PatientHistory[]) {
  //   gridDataArray.forEach( e => { if(e.createdById) {
  //     this.resourceManagementService.getProviderByExternalId(e.createdById).subscribe(
  //         p => { e.createdBy = p == null ? e.createdBy : p.findDisplayName() });
  //   } });
  // }

  presentMoreOptions(ev, historyRecord: PatientHistory) {
    const popover = this.popover.create(
      EditViewPopoverPage,
      {
        page: "PatientHistoryGridComponent",
        disabledIcon: true,
        disabledEdit: this._disableEdit,
        disabledDelete: this._disableDelete,
        hideHistory: true,
        isPatientChart: true,
      },
      { cssClass: "more-options-popover" }
    );
    popover.present({ ev });
    popover.onDidDismiss((mode) => {
      if (mode) {
        if (mode.edit) {
          this.openPopOver("PatientHistoryPopoverPage", true, historyRecord);
        } else if (mode.delete) {
          this.patientHistoryService
            .deletePatientHistoryRecord(
              this._patientId,
              this._externalId,
              historyRecord.id
            )
            .subscribe((e) => this.getPatientHistoryData());
        }
      }
    });
  }

  onArrowClick(selectedItem: PatientHistory) {
    selectedItem.toggleSelected();
  }

  firstPage() {
    if (this.currentPage !== 0) {
      this.currentPage = 0;
      this.getPatientHistoryData();
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.getPatientHistoryData();
    }
  }

  setPage(page) {
    if (page >= 0 && page <= this.totalPageCount && page !== this.currentPage) {
      this.currentPage = page;
      this.getPatientHistoryData();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPageCount - 1) {
      this.currentPage++;
      this.getPatientHistoryData();
    }
  }

  lastPage() {
    if (this.currentPage !== this.totalPageCount - 1) {
      this.currentPage = this.totalPageCount - 1;
      this.getPatientHistoryData();
    }
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });

      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-history-grid/patient-history-grid.ts